import { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Camera, Star, Trash2, ChevronLeft, ChevronRight, MapPin, Cake, Briefcase } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

const MAX_PHOTOS = 5;

function initialsFor(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  const letters = parts.length > 1 ? [parts[0][0], parts[parts.length - 1][0]] : [parts[0][0]];
  return letters.join("").toUpperCase();
}

function ageFrom(birthDate) {
  if (!birthDate) return null;
  const b = new Date(birthDate + "T00:00:00");
  if (Number.isNaN(b.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - b.getFullYear();
  const beforeBirthday = (today.getMonth() < b.getMonth()) ||
    (today.getMonth() === b.getMonth() && today.getDate() < b.getDate());
  if (beforeBirthday) age -= 1;
  return age;
}

function PhotoTile({ photo, index, total, onSetPrimary, onDelete, onMoveLeft, onMoveRight, busy }) {
  return (
    <div className="relative rounded-2xl overflow-hidden border-2 group aspect-square"
      style={{ borderColor: photo.is_primary ? "#7c3aed" : "#f0edf7" }} data-testid={`profile-photo-${index}`}>
      <img src={photo.url} alt={`Foto ${index + 1}`} className="w-full h-full object-cover" />
      {photo.is_primary && (
        <span className="absolute top-1.5 left-1.5 mc-grad text-white text-[10px] font-semibold px-2 py-0.5 rounded-full">Capa</span>
      )}
      <div className="absolute inset-x-0 bottom-0 bg-black/55 backdrop-blur-sm p-1.5 flex items-center justify-between gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
        <button type="button" title="Mover pra esquerda" disabled={index === 0 || busy} onClick={onMoveLeft}
          className="text-white p-1 rounded disabled:opacity-30" data-testid={`photo-move-left-${index}`}><ChevronLeft size={16} /></button>
        <button type="button" title="Definir como capa" disabled={photo.is_primary || busy} onClick={onSetPrimary}
          className="text-white p-1 rounded disabled:opacity-30" data-testid={`photo-primary-${index}`}><Star size={16} /></button>
        <button type="button" title="Remover" disabled={busy} onClick={onDelete}
          className="text-white p-1 rounded hover:text-red-400" data-testid={`photo-delete-${index}`}><Trash2 size={16} /></button>
        <button type="button" title="Mover pra direita" disabled={index === total - 1 || busy} onClick={onMoveRight}
          className="text-white p-1 rounded disabled:opacity-30" data-testid={`photo-move-right-${index}`}><ChevronRight size={16} /></button>
      </div>
    </div>
  );
}

export default function MeuPerfil() {
  const { user, refreshMe } = useAuth();
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [fullName, setFullName] = useState(user?.full_name || "");
  const [birthDate, setBirthDate] = useState(user?.birth_date || "");
  const [occupation, setOccupation] = useState(user?.occupation || "");
  const [bio, setBio] = useState(user?.bio || "");
  const [interests, setInterests] = useState(user?.interests || []);
  const [saving, setSaving] = useState(false);
  const [localPreview, setLocalPreview] = useState(null); // object URL enquanto a foto sobe

  const { data: photosData } = useQuery({ queryKey: ["my-photos"], queryFn: async () => (await api.get("/users/me/photos")).data });
  const photos = photosData?.items || [];

  const { data: interestsData } = useQuery({ queryKey: ["interests"], queryFn: async () => (await api.get("/interests")).data });
  const interestOptions = interestsData?.items || [];

  const invalidatePhotos = () => { qc.invalidateQueries({ queryKey: ["my-photos"] }); refreshMe(); };

  const uploadMut = useMutation({
    mutationFn: async (file) => {
      const form = new FormData();
      form.append("file", file);
      return (await api.post("/users/me/photos", form, { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: () => { toast.success("Foto adicionada!"); invalidatePhotos(); },
    onError: (err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)),
    onSettled: () => setLocalPreview((u) => { if (u) URL.revokeObjectURL(u); return null; }),
  });

  const primaryMut = useMutation({
    mutationFn: async (photoId) => (await api.post(`/users/me/photos/${photoId}/primary`)).data,
    onSuccess: () => invalidatePhotos(),
    onError: (err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)),
  });

  const deleteMut = useMutation({
    mutationFn: async (photoId) => (await api.delete(`/users/me/photos/${photoId}`)).data,
    onSuccess: () => { toast.success("Foto removida"); invalidatePhotos(); },
    onError: (err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)),
  });

  const reorderMut = useMutation({
    mutationFn: async (ids) => (await api.put("/users/me/photos/order", { ids })).data,
    onSuccess: () => invalidatePhotos(),
    onError: (err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)),
  });

  const photoBusy = uploadMut.isPending || primaryMut.isPending || deleteMut.isPending || reorderMut.isPending;

  const pickPhoto = () => fileRef.current?.click();

  const onFilePicked = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (photos.length >= MAX_PHOTOS) { toast.error(`Máximo de ${MAX_PHOTOS} fotos.`); return; }
    setLocalPreview(URL.createObjectURL(file)); // preview instantâneo enquanto sobe
    uploadMut.mutate(file);
  };

  const move = (index, dir) => {
    const next = [...photos];
    const j = index + dir;
    if (j < 0 || j >= next.length) return;
    [next[index], next[j]] = [next[j], next[index]];
    reorderMut.mutate(next.map((p) => p.id));
  };

  const toggleInterest = (label) => setInterests((cur) => cur.includes(label) ? cur.filter((x) => x !== label) : [...cur, label]);

  const dirty =
    fullName.trim() !== (user?.full_name || "") ||
    birthDate !== (user?.birth_date || "") ||
    occupation.trim() !== (user?.occupation || "") ||
    bio.trim() !== (user?.bio || "") ||
    JSON.stringify([...interests].sort()) !== JSON.stringify([...(user?.interests || [])].sort());

  const save = async (e) => {
    e.preventDefault();
    if (!dirty || !fullName.trim()) return;
    setSaving(true);
    try {
      await api.patch("/users/me", {
        full_name: fullName.trim(),
        birth_date: birthDate || null,
        occupation: occupation.trim() || null,
        bio: bio.trim() || null,
        interests,
      });
      await refreshMe();
      toast.success("Perfil atualizado!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  const previewPhoto = localPreview || user.photo_url;
  const liveAge = ageFrom(birthDate);
  const previewTags = interestOptions.filter((o) => interests.includes(o.label));

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <main className="flex-1 mc-container py-14" data-testid="page-meu-perfil">
        <div className="grid lg:grid-cols-[1fr_340px] gap-10 items-start max-w-5xl mx-auto">
          <div className="mc-card w-full p-8 mc-fade-up">
            <h1 className="text-3xl font-bold text-[#1e1b2e]">Meu Perfil</h1>
            <p className="text-muted-foreground mt-2 text-sm">
              Complete seus dados e fotos pra aparecer com destaque no Feed e ter matches mais compatíveis.
            </p>

            {/* Fotos */}
            <div className="mt-8">
              <label className="block text-sm font-semibold text-[#1e1b2e] mb-2">Fotos ({photos.length}/{MAX_PHOTOS})</label>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                {photos.map((p, i) => (
                  <PhotoTile key={p.id} photo={p} index={i} total={photos.length} busy={photoBusy}
                    onSetPrimary={() => primaryMut.mutate(p.id)}
                    onDelete={() => deleteMut.mutate(p.id)}
                    onMoveLeft={() => move(i, -1)}
                    onMoveRight={() => move(i, 1)} />
                ))}
                {uploadMut.isPending && localPreview && (
                  <div className="relative rounded-2xl overflow-hidden border-2 border-[#f0edf7] aspect-square opacity-70">
                    <img src={localPreview} alt="Enviando" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30 text-white text-xs font-semibold">Enviando...</div>
                  </div>
                )}
                {photos.length < MAX_PHOTOS && !uploadMut.isPending && (
                  <button type="button" onClick={pickPhoto} data-testid="profile-photo-add"
                    className="aspect-square rounded-2xl border-2 border-dashed border-[#d7c8fb] text-violet-600 flex flex-col items-center justify-center gap-1 hover:bg-[#f7f5fd] transition-colors">
                    <Camera size={20} />
                    <span className="text-[11px] font-semibold">Adicionar</span>
                  </button>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                data-testid="profile-photo-input" onChange={onFilePicked} />
              <p className="text-xs text-muted-foreground mt-2">
                JPG, PNG ou WEBP · máx. 5MB cada. A primeira foto vira a capa automaticamente — passe o mouse numa foto pra trocar a capa, reordenar ou remover.
              </p>
            </div>

            <form onSubmit={save} className="mt-8 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-[#1e1b2e] mb-2">Nome Completo</label>
                <input className="mc-input" data-testid="profile-name-input"
                  value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Seu nome completo" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1e1b2e] mb-2">E-mail</label>
                <input className="mc-input opacity-60 cursor-not-allowed" value={user.email} disabled data-testid="profile-email-input" />
                <p className="text-xs text-muted-foreground mt-1">Alterar e-mail ainda não é suportado.</p>
              </div>
              <div className="grid grid-cols-2 gap-4 items-end">
                <div>
                  <label className="block text-sm font-semibold text-[#1e1b2e] mb-2">Data de nascimento</label>
                  <input type="date" className="mc-input" data-testid="profile-birthdate-input"
                    value={birthDate} onChange={(e) => setBirthDate(e.target.value)} max={new Date().toISOString().slice(0, 10)} />
                </div>
                <div className="pb-2 text-sm text-muted-foreground" data-testid="profile-age-preview">
                  {liveAge !== null ? `${liveAge} anos` : "Idade calculada automaticamente"}
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1e1b2e] mb-2">Profissão</label>
                <input className="mc-input" data-testid="profile-occupation-input"
                  value={occupation} onChange={(e) => setOccupation(e.target.value)} placeholder="Ex: Designer, Engenheira, Autônomo..." />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1e1b2e] mb-2">Bio</label>
                <textarea className="mc-input" rows={3} maxLength={300} data-testid="profile-bio-input"
                  value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Conte um pouco sobre você em algumas linhas." />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1e1b2e] mb-2">Interesses</label>
                <div className="flex flex-wrap gap-2">
                  {interestOptions.map((o) => (
                    <button key={o.label} type="button" onClick={() => toggleInterest(o.label)}
                      className="mc-chip" data-active={interests.includes(o.label)} data-testid={`interest-${o.label}`}>
                      {o.icon} {o.label}
                    </button>
                  ))}
                </div>
              </div>
              <button className="btn-primary w-full" data-testid="profile-save-btn" disabled={!dirty || !fullName.trim() || saving}>
                {saving ? "Salvando..." : "Salvar alterações"}
              </button>
            </form>
          </div>

          {/* Preview do card, no mesmo estilo do Feed */}
          <div className="lg:sticky lg:top-24">
            <p className="text-sm font-semibold text-[#1e1b2e] mb-3">Assim seu card aparece no Feed</p>
            <div className="mc-card overflow-hidden flex flex-col" data-testid="profile-preview-card">
              <div className="w-full h-64 bg-[#f4f2fb]">
                {previewPhoto ? (
                  <img src={previewPhoto} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Avatar className="h-20 w-20">
                      <AvatarFallback className="mc-grad text-white text-2xl font-semibold">{initialsFor(fullName)}</AvatarFallback>
                    </Avatar>
                  </div>
                )}
              </div>
              <div className="p-5 flex flex-col flex-1">
                <h3 className="text-xl font-bold text-[#1e1b2e]">{fullName || "Seu nome"}</h3>
                {bio && <p className="text-sm text-muted-foreground mt-1">{bio}</p>}
                <ul className="mt-3 space-y-1.5 text-sm text-[#3a3550]">
                  {(user.city || user.state) && (
                    <li className="flex items-center gap-2"><MapPin size={15} className="text-violet-600" /> {[user.city, user.state].filter(Boolean).join(" – ")}</li>
                  )}
                  {liveAge !== null && <li className="flex items-center gap-2"><Cake size={15} className="text-violet-600" /> {liveAge} anos</li>}
                  {occupation && <li className="flex items-center gap-2"><Briefcase size={15} className="text-violet-600" /> {occupation}</li>}
                </ul>
                <div className="flex flex-wrap gap-2 mt-4">
                  {previewTags.map((t, i) => (
                    <span key={i} className="text-xs bg-[#f4f2fb] text-[#4b2e83] px-3 py-1 rounded-full">{t.label} {t.icon}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
